import { Teacher, TeacherAvailability, TeacherPreferences } from '../../types';
import { DayOfWeek } from '../../types';

// Mock data for Teachers page
// ~100 teachers with realistic UK names, subjects, roles, availability

const ukFirstNames = [
  'James',
  'Oliver',
  'George',
  'Noah',
  'Arthur',
  'Harry',
  'Leo',
  'Oscar',
  'Charlie',
  'Jack',
  'Emily',
  'Olivia',
  'Amelia',
  'Isla',
  'Ava',
  'Mia',
  'Isabella',
  'Sophia',
  'Grace',
  'Lily',
  'Thomas',
  'William',
  'Henry',
  'Joshua',
  'Daniel',
  'Samuel',
  'Alexander',
  'Benjamin',
  'Lucas',
  'Matthew',
  'Sophie',
  'Charlotte',
  'Ella',
  'Scarlett',
  'Freya',
  'Poppy',
  'Evie',
  'Daisy',
  'Ruby',
  'Jessica',
  'David',
  'Michael',
  'Christopher',
  'Andrew',
  'Paul',
  'Mark',
  'Richard',
  'Simon',
  'Peter',
  'Robert',
  'Sarah',
  'Emma',
  'Laura',
  'Hannah',
  'Rachel',
  'Rebecca',
  'Lucy',
  'Katie',
  'Amy',
  'Jennifer',
];

const ukSurnames = [
  'Smith',
  'Jones',
  'Taylor',
  'Brown',
  'Williams',
  'Wilson',
  'Johnson',
  'Davies',
  'Robinson',
  'Wright',
  'Thompson',
  'Evans',
  'Walker',
  'White',
  'Roberts',
  'Green',
  'Hall',
  'Wood',
  'Jackson',
  'Clarke',
  'Patel',
  'Khan',
  'Ahmed',
  'Ali',
  'Hussain',
  'Shah',
  'Begum',
  'Singh',
  'Kaur',
  'Sharma',
  'Murphy',
  "O'Brien",
  'Kelly',
  'Ryan',
  'Walsh',
  'McCarthy',
  "O'Connor",
  "O'Sullivan",
  'Byrne',
  'Martin',
  'Anderson',
  'Campbell',
  'Scott',
  'Mitchell',
  'Young',
  'King',
  'Edwards',
  'Hughes',
  'Lewis',
  'Morris',
];

const subjects = [
  'Mathematics',
  'English',
  'Science',
  'Physics',
  'Chemistry',
  'Biology',
  'History',
  'Geography',
  'French',
  'Spanish',
  'German',
  'Art',
  'Music',
  'Drama',
  'PE',
  'ICT',
  'Design Technology',
  'RE',
  'PSHE',
  'Business Studies',
  'Economics',
  'Psychology',
];

const generateTeacherAvailability = (contractType: 'full-time' | 'part-time' | 'supply'): TeacherAvailability => {
  if (contractType === 'full-time') {
    return {
      monday: { available: true, periods: [1, 2, 3, 4, 5] },
      tuesday: { available: true, periods: [1, 2, 3, 4, 5] },
      wednesday: { available: true, periods: [1, 2, 3, 4, 5] },
      thursday: { available: true, periods: [1, 2, 3, 4, 5] },
      friday: { available: true, periods: [1, 2, 3, 4, 5] },
    };
  }
  if (contractType === 'part-time') {
    // Part-time: 3 days a week
    return {
      monday: { available: true, periods: [1, 2, 3, 4, 5] },
      tuesday: { available: true, periods: [1, 2, 3, 4, 5] },
      wednesday: { available: false, periods: [] },
      thursday: { available: true, periods: [1, 2, 3, 4, 5] },
      friday: { available: false, periods: [] },
    };
  }
  // Supply: varied availability
  return {
    monday: { available: true, periods: [1, 2, 3] },
    tuesday: { available: false, periods: [] },
    wednesday: { available: true, periods: [3, 4, 5] },
    thursday: { available: true, periods: [1, 2] },
    friday: { available: false, periods: [] },
  };
};

const generateTeacherPreferences = (): TeacherPreferences => {
  const preferredDays: DayOfWeek[] = [];
  const avoidedDays: DayOfWeek[] = [];

  // Random preferences
  if (Math.random() > 0.7) preferredDays.push('wednesday');
  if (Math.random() > 0.8) avoidedDays.push('friday');

  return {
    preferredDays,
    avoidedDays,
    preferredPeriods: Math.random() > 0.5 ? [1, 2] : [],
    avoidedPeriods: Math.random() > 0.5 ? [5] : [],
    maxConsecutiveLessons: Math.random() > 0.5 ? 3 : undefined,
    minBreakBetweenLessons: Math.random() > 0.5 ? 1 : undefined,
    preferredRooms: [],
    notes: Math.random() > 0.7 ? 'Prefers morning teaching' : undefined,
  };
};

export const mockTeachers: Teacher[] = [];

// Generate 100 teachers
for (let i = 0; i < 100; i++) {
  const firstName = ukFirstNames[Math.floor(Math.random() * ukFirstNames.length)];
  const lastName = ukSurnames[Math.floor(Math.random() * ukSurnames.length)];
  const contractType: 'full-time' | 'part-time' | 'supply' = i < 70 ? 'full-time' : i < 90 ? 'part-time' : 'supply';

  // Assign 1-3 subjects
  const teacherSubjects = [];
  const numSubjects = Math.floor(Math.random() * 3) + 1;
  for (let j = 0; j < numSubjects; j++) {
    const subject = subjects[Math.floor(Math.random() * subjects.length)];
    if (!teacherSubjects.includes(subject)) {
      teacherSubjects.push(subject);
    }
  }

  // Assign roles
  const roles = [];
  if (i < 5) roles.push('SLT');
  if (i >= 5 && i < 20) roles.push('HoD');
  if (i >= 20 && i < 35) roles.push('HoY');
  roles.push('Teacher');

  const maxPeriodsPerWeek = contractType === 'full-time' ? 25 : contractType === 'part-time' ? 15 : 10;

  const teacher: Teacher = {
    id: `teacher-${i + 1}`,
    firstName,
    lastName,
    email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@school.ac.uk`,
    phone: `07${Math.floor(Math.random() * 900000000 + 100000000)}`,
    subjects: teacherSubjects,
    qualifications: [`PGCE in ${teacherSubjects[0]}`, 'QTS'],
    roles,
    contractType,
    maxPeriodsPerWeek,
    contractedDays: contractType === 'part-time' ? ['monday', 'tuesday', 'thursday'] : undefined,
    availability: generateTeacherAvailability(contractType),
    preferences: generateTeacherPreferences(),
  };

  mockTeachers.push(teacher);
}

// Ensure we have good distribution of subjects
const _mathsTeachers = mockTeachers.filter((t) => t.subjects.includes('Mathematics')).length;
const _englishTeachers = mockTeachers.filter((t) => t.subjects.includes('English')).length;
const _scienceTeachers = mockTeachers.filter((t) => t.subjects.includes('Science')).length;

export default mockTeachers;
